import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Axios } from "../../../Api/axios";
import {
  ADMIN_ONE_INTENSIVE,
  ALLSTD,
  ADMIN_ENROLL_INTENSIVE,
  ADMIN_UNENROLL_INTENSIVE,
  ADMIN_INTENSIVE_STUDENTS,
  baseURL,
} from "../../../Api/Api";
import LoadingSubmit from "../../../Components/Loading/Loading";
import "../dashboard.css";
import "./EnrollIntensive.css";

export default function EnrollIntensive() {
  const { id } = useParams();
  const nav = useNavigate();

  const [courseName, setCourseName] = useState("");
  const [allStudents, setAllStudents] = useState([]);
  const [enrolled, setEnrolled] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    Axios.get(ADMIN_ONE_INTENSIVE(id))
      .then((res) => setCourseName(res.data.one_intensive?.name || `#${id}`))
      .catch(() => setCourseName(`#${id}`));
  }, [id]);

  function loadAll() {
    setLoading(true);
    Promise.all([
      Axios.get(`${ALLSTD}?limit=1000`),
      Axios.get(ADMIN_INTENSIVE_STUDENTS(id)),
    ])
      .then(([studentsRes, enrolledRes]) => {
        setAllStudents(studentsRes.data.students || []);
        setEnrolled(enrolledRes.data.students || []);
      })
      .catch(() => { setAllStudents([]); setEnrolled([]); })
      .finally(() => setLoading(false));
  }

  useEffect(() => { loadAll(); }, [id]);

  const enrolledIds = useMemo(() => new Set(enrolled.map((s) => s.id)), [enrolled]);

  const pickableStudents = useMemo(() => {
    const q = search.trim();
    return allStudents
      .filter((s) => !enrolledIds.has(s.id))
      .filter((s) => !q || s.name?.includes(q) || s.phone?.includes(q));
  }, [allStudents, enrolledIds, search]);

  async function handleEnroll() {
    if (!selectedId) return;
    setBusy(true);
    setErr("");
    try {
      await Axios.post(ADMIN_ENROLL_INTENSIVE, { UserId: selectedId, IntensiveCourseId: id });
      setSelectedId("");
      loadAll();
    } catch (e) {
      setErr(e?.response?.data?.message || "حدث خطأ أثناء التسجيل");
    } finally {
      setBusy(false);
    }
  }

  async function handleUnenroll(studentId) {
    if (!window.confirm("هل تريد إلغاء تسجيل هذا الطالب؟")) return;
    setBusy(true);
    setErr("");
    try {
      await Axios.post(ADMIN_UNENROLL_INTENSIVE, { UserId: studentId, IntensiveCourseId: id });
      setEnrolled((prev) => prev.filter((s) => s.id !== studentId));
    } catch (e) {
      setErr(e?.response?.data?.message || "حدث خطأ أثناء إلغاء التسجيل");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="dash-page">
      {busy && <LoadingSubmit />}
      <div className="dash-page-header">
        <div>
          <h1 className="dash-page-title">تسجيل الطلاب بالدورة المكثفة</h1>
          <p className="il-subtitle">{courseName}</p>
        </div>
        <button className="dash-add-btn" onClick={() => nav(-1)} style={{ background: "#374151" }}>
          رجوع
        </button>
      </div>

      {err && <div className="il-error" style={{ marginBottom: 16 }}>{err}</div>}

      {/* Enroll form */}
      <div className="table-card ei-add-card">
        <h3 className="il-section-title">تسجيل طالب جديد</h3>
        <div className="ei-picker-row">
          <input
            type="search"
            className="il-url-input ei-search-input"
            placeholder="ابحث بالاسم أو الهاتف..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select
            className="ei-select"
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
          >
            <option value="">اختر طالباً...</option>
            {pickableStudents.map((s) => (
              <option key={s.id} value={s.id}>{s.name} {s.phone ? `— ${s.phone}` : ""}</option>
            ))}
          </select>
          <button
            className="dash-add-btn ei-enroll-btn"
            onClick={handleEnroll}
            disabled={!selectedId || busy}
          >
            تسجيل
          </button>
        </div>
        {!loading && pickableStudents.length === 0 && (
          <p className="il-muted" style={{ marginTop: 10 }}>
            {allStudents.length === 0 ? "لا يوجد طلاب" : "كل الطلاب المطابقين مسجّلون بالفعل"}
          </p>
        )}
      </div>

      {/* Roster */}
      <div className="table-card ei-roster-card">
        <h3 className="il-section-title">الطلاب المسجّلون ({enrolled.length})</h3>
        {loading ? (
          <p className="il-muted">جاري التحميل...</p>
        ) : enrolled.length === 0 ? (
          <p className="il-muted">لا يوجد طلاب مسجّلون بعد</p>
        ) : (
          <div className="ei-roster-list">
            {enrolled.map((s) => (
              <div key={s.id} className="ei-roster-row">
                {s.photo ? (
                  <img className="row-thumb" src={`${baseURL}/uploads/students/${s.photo}`} alt="" />
                ) : (
                  <span className="id-badge">{s.name?.[0] || "?"}</span>
                )}
                <div className="ei-roster-info">
                  <span className="ei-roster-name">{s.name}</span>
                  {s.phone && <span className="ei-roster-phone">{s.phone}</span>}
                </div>
                <button
                  className="action-btn action-delete"
                  onClick={() => handleUnenroll(s.id)}
                  disabled={busy}
                  title="إلغاء التسجيل"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
